import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { fetchWxAuthUrl } from "@/api/wechat";

export default function WxAuthPage() {
  const [searchParams] = useSearchParams();
  const [redirecting, setRedirecting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // 支持从 URL 的 redirect 参数读取授权后回跳地址
  const redirectTarget = searchParams.get("redirect") || "/profile";

  /** 授权回调地址：当前域名 + /wx-callback */
  function getCallbackUrl(): string {
    return `${window.location.origin}${__BASE_PATH__ || "/"}wx-callback`;
  }

  const handleAuth = async () => {
    if (redirecting) return;

    try {
      setRedirecting(true);
      setErrorMsg("");

      // 调用接口1获取微信授权跳转地址
      const callbackUrl = getCallbackUrl();
      // 将 redirect 参数拼到回调 URL 上
      const separator = callbackUrl.includes("?") ? "&" : "?";
      const fullCallback = `${callbackUrl}${separator}redirect=${encodeURIComponent(redirectTarget)}`;

      const { authorize_url } = await fetchWxAuthUrl(fullCallback);

      // 跳转到微信授权页面
      window.location.href = authorize_url;
    } catch (err) {
      console.error("[WxAuth] 获取授权地址失败:", err);
      setRedirecting(false);
      setErrorMsg(err instanceof Error ? err.message : "获取授权链接失败");
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{
        background:
          "linear-gradient(160deg, #0D0D1A 0%, #1A0A00 50%, #0D0D1A 100%)",
      }}
    >
      {/* 背景光晕 */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute rounded-full"
          style={{
            width: 450,
            height: 450,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background:
              "radial-gradient(circle, rgba(37,99,235,0.12) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* 主卡片 */}
      <div className="relative z-10 w-full max-w-sm">
        {/* 微信图标 */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-5">
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background: "linear-gradient(135deg, #07C160, #06AE56)",
                filter: "blur(18px)",
                opacity: 0.45,
                transform: "scale(1.35)",
              }}
            />
            <div
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #07C160, #06AE56)",
              }}
            >
              <i
                className="ri-wechat-fill text-white"
                style={{ fontSize: 38 }}
              />
            </div>
          </div>
          <h1
            className="text-2xl font-bold text-white tracking-wide mb-2"
            style={{
              fontFamily: "'SF Pro Display', 'PingFang SC', sans-serif",
            }}
          >
            微信授权登录
          </h1>
          <p
            className="text-sm text-center"
            style={{ color: "rgba(255,255,255,0.45)" }}
          >
            为了提供更好的服务，需要获取您的微信信息
          </p>
        </div>

        {/* 信息卡片 */}
        <div
          className="rounded-2xl p-5 mb-8"
          style={{
            background: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <div className="space-y-4">
            {[
              {
                icon: "ri-user-line",
                label: "获取您的头像与昵称",
                desc: "用于展示您的个人资料",
              },
              {
                icon: "ri-shield-check-line",
                label: "安全授权",
                desc: "数据加密传输，隐私安全有保障",
              },
              {
                icon: "ri-time-line",
                label: "仅需一次",
                desc: "首次授权后后续无需重复确认",
              },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: "rgba(7,193,96,0.12)" }}
                >
                  <i
                    className={item.icon}
                    style={{ color: "#07C160", fontSize: 18 }}
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-white/90">
                    {item.label}
                  </div>
                  <div
                    className="text-xs mt-0.5"
                    style={{ color: "rgba(255,255,255,0.35)" }}
                  >
                    {item.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 错误提示 */}
        {errorMsg && (
          <div
            className="mb-4 px-4 py-3 rounded-xl text-xs text-center"
            style={{ background: "rgba(239,68,68,0.1)", color: "#F87171" }}
          >
            {errorMsg}
          </div>
        )}

        {/* 授权按钮 */}
        <button
          onClick={handleAuth}
          disabled={redirecting}
          className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-2xl cursor-pointer active:scale-[0.97] transition-all whitespace-nowrap"
          style={{
            background: redirecting
              ? "rgba(255,255,255,0.1)"
              : "linear-gradient(135deg, #07C160, #06AE56)",
            boxShadow: redirecting ? "none" : "0 8px 28px rgba(7,193,96,0.3)",
            border: "none",
          }}
        >
          {redirecting ? (
            <>
              <i
                className="ri-loader-4-line animate-spin"
                style={{ color: "#fff", fontSize: 18 }}
              />
              <span className="text-sm font-semibold text-white">
                正在跳转到微信...
              </span>
            </>
          ) : (
            <>
              <i
                className="ri-wechat-fill"
                style={{ color: "#fff", fontSize: 17 }}
              />
              <span className="text-base font-semibold text-white">
                一键授权微信信息
              </span>
            </>
          )}
        </button>

        {/* 底部说明 */}
        <p
          className="text-center text-[11px] mt-5"
          style={{ color: "rgba(255,255,255,0.22)" }}
        >
          点击即表示您同意《用户协议》和《隐私政策》
        </p>
      </div>
    </div>
  );
}
